import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { RootState } from 'store';

import { fetchUserAccount } from 'actions/user';
import { ReactComponent as IconAutos } from 'assets/images/rappi_autos.svg';
import { ReactComponent as IconFlights } from 'assets/images/rappi_flights.svg';
import { ReactComponent as IconHotels } from 'assets/images/rappi_hotels.svg';
import DesktopAutosSearch from 'containers/DesktopAutosSearch';
import DesktopFlightsSearch from 'containers/DesktopFlightsSearch';
import DesktopHotelsSearch from 'containers/DesktopHotelsSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { amplitudelogEvent, ViewTravelSearchScreenEvent } from 'services/amplitudeService';
import { getAllBillingCompanies } from 'services/billingCompanyService';
import { getAllowedTravelers, TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';

import DesktopSearchBarTabs, { TabData } from './DesktopSearchBarTabs';
import { Container } from './styles';

export enum SearchSwitchEnum {
  Flights = 'flights',
  Hotels = 'hotels',
  Autos = 'autos',
}

export interface Props {
  activeTab?: SearchSwitchEnum;
  isLoading?: boolean;
}

const DesktopSearchBar = ({ activeTab = SearchSwitchEnum.Flights, isLoading }: Props) => {
  const { t } = useStoreTranslation();
  const dispatch = useDispatch();
  const user = useUserAccount();
  const history = useHistory<{ errorMessage?: string } | null>();

  const [currentSwitch, setCurrentSwitch] = useState(activeTab);

  const [allowedTravelers, setAllowedTravelers] = useState<TravelArrangerUsersAllowed[]>([]);
  const [isLoadingAllowedTravelers, setLoadingAllowedTravelers] = useState(true);
  const [isLoadedAllowedTravelers, setLoadedAllowedTravelers] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [branchCompanies, setBranchCompanies] = useState<GetAllBillingCompanyResponse[]>([]);
  const [isLoadingBranchCompanies, setIsLoadingBranchCompanies] = useState(false);
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);

  const getAmplitudeEvent = (type: SearchSwitchEnum) =>
    type === SearchSwitchEnum.Flights ? 'FLIGHTS' : type === SearchSwitchEnum.Hotels ? 'HOTELS' : 'CAR_RENTAL';

  const handleBookingTypeChange = (type: SearchSwitchEnum) => {
    setCurrentSwitch(type);
    //history.replace(`/booking/${type === SearchSwitchEnum.Hotels ? 'hotels' : 'flights'}`);

    sendAmplitudeEvent(getAmplitudeEvent(type));
  };

  useEffect(() => {
    if (user) {
      setUserLoaded(true);
    } else {
      const fetch = async () => {
        await dispatch(fetchUserAccount());
        setUserLoaded(true);
      };
      fetch();
    }
  }, [dispatch, user]);

  const sendAmplitudeEvent = (productType: 'FLIGHTS' | 'HOTELS' | 'CAR_RENTAL') => {
    //Amplitude
    const event: ViewTravelSearchScreenEvent = {
      SOURCE: 'TRAVEL_HOME',
      TRAVEL_PRODUCT_TYPE: productType,
    };

    amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_SEARCH_SCREEN, event, user);
  };

  const searchBar = () => {
    const options = [
      {
        label: t('booking.flights'),
        value: SearchSwitchEnum.Flights,
        icon: <IconFlights />,
        onClick: handleBookingTypeChange,
        testId: 'booking-flight-tab',
      } as TabData,
      {
        label: t('booking.hotels'),
        value: SearchSwitchEnum.Hotels,
        icon: <IconHotels />,
        onClick: handleBookingTypeChange,
        testId: 'booking-hotel-tab',
      } as TabData,
      {
        label: t('booking.autos'),
        value: SearchSwitchEnum.Autos,
        icon: <IconAutos />,
        onClick: handleBookingTypeChange,
      } as TabData,
    ];
    const current = options.findIndex((opt) => opt.value === currentSwitch);
    return <DesktopSearchBarTabs options={options} current={current > -1 ? current : 0} />;
  };

  useEffect(() => {
    if (history.location.pathname.indexOf('/hotels') > -1) setCurrentSwitch(SearchSwitchEnum.Hotels);
    if (history.location.pathname.indexOf('/autos') > -1) setCurrentSwitch(SearchSwitchEnum.Autos);
  }, [history]);

  useEffect(() => {
    const fetch = async () => {
      if (!userLoaded) return;
      setLoadedAllowedTravelers(true);

      if (!user?.isTravelArranger) {
        setLoadingAllowedTravelers(false);
        return;
      }

      const { data } = await getAllowedTravelers();

      setAllowedTravelers(data);
      setLoadingAllowedTravelers(false);
    };

    if (!isLoadedAllowedTravelers) fetch();
  }, [isLoadedAllowedTravelers, user, userLoaded]);

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchUserAccount());
      setUserLoaded(true);
    };
    fetch();
  }, [dispatch]);

  useEffect(() => {
    setIsLoadingBranchCompanies(true);
    getAllBillingCompanies(companyId ?? '', true).then((res) => {
      setBranchCompanies(res);
      setIsLoadingBranchCompanies(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      {searchBar()}
      {currentSwitch === SearchSwitchEnum.Flights ? (
        <DesktopFlightsSearch
          allowedTravelers={allowedTravelers}
          isLoadingAllowedTravelers={isLoadingAllowedTravelers}
          isLoading={isLoading}
          isLoadingBranchCompanies={isLoadingBranchCompanies}
          branchCompanies={branchCompanies}
        />
      ) : currentSwitch === SearchSwitchEnum.Hotels ? (
        <DesktopHotelsSearch
          allowedTravelers={allowedTravelers}
          isLoadingAllowedTravelers={isLoadingAllowedTravelers}
          isLoading={isLoading}
          isLoadingBranchCompanies={isLoadingBranchCompanies}
          branchCompanies={branchCompanies}
        />
      ) : (
        <DesktopAutosSearch
          allowedTravelers={allowedTravelers}
          isLoadingAllowedTravelers={isLoadingAllowedTravelers}
          isLoading={isLoading}
          isLoadingBranchCompanies={isLoadingBranchCompanies}
          branchCompanies={branchCompanies}
        />
      )}
    </Container>
  );
};

export default DesktopSearchBar;
